//@ts-check
import React, { useContext } from 'react'
import { useDispatch } from 'react-redux'
import { SnackBarContext } from '../../../../../../../Components/SnackBar'
import { apiClient } from '../../../../../../../Module/API/Client'
import { updateCampaignExtraData } from '../../../../../../Campaigns/Page/Codes/gql/updateCampaignInternalStatus'
import { showReduxModal } from '../../../../../../../Reducers/UI/actions'
import {
  CAMPAIGNS_WITH_NO_PAYMENT,
  handleUpdateExtraData,
  saveCustomLocations,
  splitEmails,
  useCancelPayment,
} from '../../utils'
import { EMAIL_TYPE } from '../../options'
import { useStyles } from '../../style'
import {
  createCityPlanAndMissionOnSSCampaign,
  createEDDMInternalFlyerCodes,
  sendEmail,
  updateCampaignsCampaignBuilderData,
  updateSelfServeCampaignData,
} from '../../graphQL'
import {
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_INTERNAL_STATUSES,
  CAMPAIGN_LOG_ENUM_TYPES,
  CAMPAIGN_SUBTYPES_ENUM,
} from '../../../../../../../shared/constants'
import { authorization } from '../../../../../../../Module/Auth/Auth'
import { insertCampaignLogs } from '../../../../../../../Logs/campaign/gql'
import { queryClient } from '../../../../../../../Module/Query'
import { CONFIG } from '../../../../../../../Config/config'
import { apiXero } from '../../../../../../../Module/API/Xero'
import { isProduction } from '../../../../../../../Utils/Functions'
import { getCampaignCityMetrics } from '../../../../../../Campaigns/CreateEditCampaign/gql'
import { handleConfirmationDialog } from '../../../../../../City/Page/Ambasadors/EmailPipelineView/utils'

export function useOnSubmit({
  setIsLoadingRequest,
  campaignEmailData,
  setCampaignEmailData,
  tenant,
  setConfirmDialogData,
}) {
  const runSnackBar = useContext(SnackBarContext)
  const { cancelPayment } = useCancelPayment()
  const { capturePayment } = useCapturePayment()
  const { onSendEmail } = useOnSendEmail()

  async function onSubmit({
    data,
    sendEmail,
    emailType,
    campaignId,
    paymentIntent,
    isPaymentSkipped,
    clientId,
    isMoveInGenerationDigitalProof,
    type,
    subtype,
    countryCode,
    userId,
  }) {
    setIsLoadingRequest(true)
    const isDirectMail =
      campaignEmailData.subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM ||
      campaignEmailData.subtype === CAMPAIGN_SUBTYPES_ENUM.ADM

    if (emailType === EMAIL_TYPE.APPROVE_CAMPAIGN) {
      if (!isDirectMail) {
        const campaignCityMetricsData = await getCampaignCityMetrics(
          campaignEmailData.campaignId,
        )

        if (!campaignCityMetricsData.length) {
          handleConfirmationDialog({
            title: 'Restricted!',
            contentText:
              'You cannot approve campaign without filled cities metrics',
            setConfirmDialogData,
            rightButtonLable: 'OK',
          })

          setIsLoadingRequest(false)
          return
        }

        if (!tenant && isProduction()) {
          handleConfirmationDialog({
            title: 'Restricted!',
            contentText: 'You cannot approve campaign without Xero connection',
            setConfirmDialogData,
            rightButtonLable: 'OK',
          })
          setIsLoadingRequest(false)
          return
        }
      }
    }

    if (sendEmail) {
      await onSendEmail({
        ...data,
        isShowToast: ![
          EMAIL_TYPE.DECLINE_CAMPAIGN,
          EMAIL_TYPE.APPROVE_CAMPAIGN,
        ].includes(emailType),
      })
    }

    if (emailType === EMAIL_TYPE.DECLINE_CAMPAIGN) {
      try {
        if (type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL) {
          const rejectDmResult = await handleRejectDmCampaign({
            campaignId,
            subtype,
          })
          if (rejectDmResult && !!rejectDmResult.error) {
            throw new Error(
              'There was an error while deleting Direct Mail campaign data',
            )
          }
        }
        if (
          !isPaymentSkipped &&
          !CAMPAIGNS_WITH_NO_PAYMENT.includes(campaignId)
        ) {
          await cancelPayment({
            campaignId,
            paymentIntent,
            countryCode,
            userId,
          })
        }
        await handleUpdateExtraData({
          campaignId,
          internalStatus: CAMPAIGN_INTERNAL_STATUSES.REJECTED,
          paymentStatus: 'rejected',
          lastActiveStep: isDirectMail ? 'segments' : 'details',
        })
        await insertCampaignLogs([
          {
            campaignId,
            type: CAMPAIGN_LOG_ENUM_TYPES.DECLINE_CAMPAIGN,
            additionalInfo: sendEmail ? 'with' : 'without',
          },
        ])
        try {
          await updateSelfServeCampaignData({
            campaignId,
            campaignDetailedCosts: null,
          })
        } catch (error) {
          console.error('Error updating self serve campaign data', error)
        }
        if (sendEmail) {
          await handleSaveDeclineCampaignEmail({
            campaignId,
            email: JSON.stringify(data.body.value),
          })
          runSnackBar({
            type: 'success',
            msg: `Email sent! Campaign declined & moved back to Draft`,
          })
        } else {
          runSnackBar({
            type: 'success',
            msg: 'Email NOT sent! Campaign declined & moved back to Draft',
          })
        }
      } catch (error) {
        runSnackBar({
          type: 'error',
          msg: `There was an error while rejecting the campaign, please contact support`,
        })
        console.error({ error })
      }
    }

    if (emailType === EMAIL_TYPE.APPROVE_CAMPAIGN) {
      try {
        if (
          campaignEmailData.subtype !== CAMPAIGN_SUBTYPES_ENUM.EDDM &&
          campaignEmailData.subtype !== CAMPAIGN_SUBTYPES_ENUM.ADM
        ) {
          runSnackBar({
            type: 'success',
            msg: `Creation city plan and missions`,
          })
          const reqIsCreateCityPlanAndMissions = await createCityPlanAndMissionOnSSCampaign(
            {
              campaignId,
            },
          )

          const { data } = await reqIsCreateCityPlanAndMissions.json()

          if (!data.createCityPlanAndMissionOnSSCampaign.success) {
            setCampaignEmailData(null)
            return runSnackBar({
              type: 'error',
              msg: `Cannot create city plan and missions`,
            })
          }
        } else {
          // Handle direct mail campaign approval
          try {
            await handleSetDMCampaignApprovedTask({
              campaignId,
              subtype: campaignEmailData.subtype,
              userId,
            })
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(
              'Error setting direct mail campaign approved task',
              error,
            )
          }

          if (campaignEmailData.subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM) {
            // TODO: ADD LOGIC FOR CREATING ADM INTERNAL FLYER CODES
            const createCodesRequest = await createEDDMInternalFlyerCodes({
              campaignId,
              clientId,
            })

            const {
              data: {
                createEDDMInternalFlyerCodes: { success },
              },
            } = await createCodesRequest.json()

            if (!success) {
              setCampaignEmailData(null)
              return runSnackBar({
                type: 'error',
                msg: `Cannot create internal flyer codes`,
              })
            }
          }
        }

        if (
          !isPaymentSkipped &&
          !CAMPAIGNS_WITH_NO_PAYMENT.includes(campaignId)
        ) {
          await capturePayment({
            campaignId,
            paymentIntent,
            countryCode,
            userId,
          })
        }

        const updateCampaignExtraDataInput = {
          campaignId,
          internalStatus: isMoveInGenerationDigitalProof
            ? CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF
            : CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW,
        }

        const logs = [
          {
            campaignId,
            type: CAMPAIGN_LOG_ENUM_TYPES.APPROVE_CAMPAIGN,
            additionalInfo: sendEmail ? 'with' : 'without',
          },
        ]

        if (isPaymentSkipped) {
          updateCampaignExtraDataInput['paymentStatus'] = 'succeeded'
          logs.push({
            campaignId,
            type: CAMPAIGN_LOG_ENUM_TYPES.SET_PAYMENT_STATUS,
            additionalInfo: `Payment was skipped status changed to [succeeded]`,
          })
        }

        await handleUpdateExtraData(updateCampaignExtraDataInput)
        await insertCampaignLogs(logs)
        if (
          campaignEmailData.subtype !== CAMPAIGN_SUBTYPES_ENUM.EDDM &&
          campaignEmailData.subtype !== CAMPAIGN_SUBTYPES_ENUM.ADM
        ) {
          await saveCustomLocations({ campaignId, clientId })
        }
        if (sendEmail) {
          runSnackBar({
            type: 'success',
            msg: `Email sent! Campaign approved & moved to ${
              isMoveInGenerationDigitalProof
                ? 'Generating Digital Proof'
                : 'Code Review'
            }`,
          })
        } else {
          runSnackBar({
            type: 'success',
            msg: `Email NOT sent! Campaign approved & moved to ${
              isMoveInGenerationDigitalProof
                ? 'Generating Digital Proof'
                : 'Code Review'
            }`,
          })
        }

        if (tenant && apiXero.isHaveXeroAccess()) {
          runSnackBar({
            type: 'success',
            msg: `Starting create Xero data`,
          })

          const xeroData = await apiXero.creatingContactAndItemAndTrackingAndInvoice(
            {
              campaignId: campaignEmailData.campaignId,
              tenantId: tenant.tenantId,
            },
          )
          if (xeroData.success) {
            runSnackBar({
              type: 'success',
              msg: `All Xero data created successfully`,
            })
          } else {
            runSnackBar({
              type: 'error',
              msg: `All Xero data not created successfully`,
            })
          }
        }
      } catch (error) {
        console.error({ error })
      }
    }
    setIsLoadingRequest(false)
    setCampaignEmailData(null)
  }
  return { onSubmit }
}

function useCapturePayment() {
  const runSnackBar = useContext(SnackBarContext)

  async function capturePayment({
    campaignId,
    paymentIntent,
    countryCode,
    userId,
  }) {
    if (!paymentIntent) {
      runSnackBar({
        type: 'error',
        msg: `Missing Stripe Payment Intent error`,
      })
      throw Error('Missing Stripe Payment Intent error')
    }

    const res = await apiClient.capturPayment({
      paymentIntent,
      countryCode,
      campaignId,
      userId,
    })

    if (res && res.status === 200) {
      const { paymentStatus } = await res.json()
      runSnackBar({
        type: 'success',
        msg: `Payment successful`,
      })
      return await updateCampaignExtraData({
        campaignId,
        paymentStatus,
      })
    }

    runSnackBar({
      type: 'error',
      msg: `Payment error`,
    })
    throw Error('Payment error')
  }

  return { capturePayment }
}

function useOnSendEmail() {
  const dispatch = useDispatch()
  const runSnackBar = useContext(SnackBarContext)
  const classes = useStyles()

  async function onSendEmail({
    sender: { value: sender },
    recipients: { value: recipients },
    bcc: { value: bcc },
    cc: { value: cc },
    subject: { value: subject },
    body: { value: body },
    isShowToast = true,
    attachments,
  }) {
    const response = await sendEmail({
      input: {
        sender,
        recipients: splitEmails(recipients),
        bcc: splitEmails(bcc),
        cc: splitEmails(cc),
        subject,
        body: JSON.stringify(body),
        attachments,
      },
    })

    const { data: result } = await response.json()

    const { sendedEmailsTo, failedEmailsTo } = result.sendEmail.reduce(
      (acc, { success, recipient }) => {
        if (!success) {
          if (acc.failedEmailsTo.length) {
            acc.failedEmailsTo += `, ${recipient}`
          } else {
            acc.failedEmailsTo += recipient
          }
        } else {
          if (acc.sendedEmailsTo.length) {
            acc.sendedEmailsTo += `, ${recipient}`
          } else {
            acc.sendedEmailsTo += recipient
          }
        }
        return acc
      },
      { sendedEmailsTo: '', failedEmailsTo: '' },
    )

    if (failedEmailsTo.length) {
      dispatch(
        showReduxModal({
          title: `Error on send email`,
          children: (
            <div className={classes.modalText}>
              {sendedEmailsTo ? (
                <div>{`Successfully send to ${sendedEmailsTo}`}</div>
              ) : null}
              <br />
              <div>
                Cannot send email to{' '}
                <span className={classes.error}>{failedEmailsTo}</span>
              </div>
            </div>
          ),
          confirmLabel: `OK`,
          hideCancel: true,
        }),
      )
    } else {
      if (isShowToast) {
        runSnackBar({
          type: 'success',
          msg: `Email sent successfully`,
        })
      }
    }
  }
  return { onSendEmail }
}

async function handleSaveDeclineCampaignEmail({ campaignId, email }) {
  const response = await updateCampaignsCampaignBuilderData({
    input: {
      campaignId,
      declineCampaignEmail: email,
    },
  })
  const { data: result } = await response.json()

  if (result.updateCampaignExtraData.success) {
    queryClient.invalidateQueries('CampaignPipelineData')
  } else {
    throw new Error(result.updateCampaignExtraData.error)
  }
}

async function handleRejectDmCampaign({ campaignId, subtype }) {
  const isAdm = subtype === CAMPAIGN_SUBTYPES_ENUM.ADM
  const token = authorization.getAPIKey()
  const url = `${CONFIG.API_BASE_URL}/campaigns/us/direct-mail${
    isAdm ? '/adm' : ''
  }/${campaignId}/reject`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-api-key': token,
    },
  })
  const result = await response.json()

  return result
}

async function handleSetDMCampaignApprovedTask({
  campaignId,
  subtype,
  userId,
}) {
  const isAdm = subtype === CAMPAIGN_SUBTYPES_ENUM.ADM
  const token = authorization.getAPIKey()
  const url = `${CONFIG.API_BASE_URL}/campaigns/us/direct-mail${
    isAdm ? '/adm' : '/eddm'
  }/${campaignId}/set-approve-task`

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-api-key': token,
    },
    body: JSON.stringify({ userId }),
  })
  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.message || 'Failed to approve campaign')
  }

  return result
}
